import { useOne, useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import type { ProfileOut, UserOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { PROFILE_DEFAULT_TAB, PROFILE_SHOW_TABS } from '../constants';
import { ProfileShowHeader } from './ProfileShowHeader';
import { ProfileGeneralTab } from './ProfileGeneralTab';
import { ProfileFavoritesTab } from './ProfileFavoritesTab';
import { ProfileCollectionsTab } from './ProfileCollectionsTab';
import { ProfileProgressionsTab } from './ProfileProgressionsTab';
import { ProfileSessionsTab } from './ProfileSessionsTab';
import { ProfileCyclesTab } from './ProfileCyclesTab';
import { ProfileReviewsTab } from './ProfileReviewsTab';

export const ProfileShow = () => {
  const { token } = theme.useToken();
  const { query } = useShow<ProfileOut>({ resource: 'account.profiles' });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle(
    'account.profiles',
    record ? (record.name ?? `#${record.id}`) : undefined
  );

  const [activeTab, onTabChange] = useShowTab(
    PROFILE_SHOW_TABS,
    PROFILE_DEFAULT_TAB
  );

  const { result: user } = useOne<UserOut>({
    resource: 'iam.users',
    id: record?.user_id ?? 0,
    queryOptions: { enabled: !!record?.user_id },
  });

  return (
    <Show isLoading={isLoading} contentProps={bareShowContentProps}>
      {record && (
        <>
          <ProfileShowHeader
            record={record}
            user={user}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'profile' && (
              <ProfileGeneralTab record={record} user={user} />
            )}
            {activeTab === 'favorites' && (
              <ProfileFavoritesTab record={record} />
            )}
            {activeTab === 'collections' && (
              <ProfileCollectionsTab record={record} />
            )}
            {activeTab === 'progressions' && (
              <ProfileProgressionsTab profileId={record.id} />
            )}
            {activeTab === 'sessions' && (
              <ProfileSessionsTab profileId={record.id} />
            )}
            {activeTab === 'cycles' && (
              <ProfileCyclesTab profileId={record.id} />
            )}
            {activeTab === 'reviews' && (
              <ProfileReviewsTab profileId={record.id} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
